import { useState } from "react";
import { Button } from "@/components/ui/button"; 
import { Link } from "react-router-dom";
import { FileText, Building, Clock, FileCheck, MessageCircle, User, Menu, X } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import ServicePageLinks from "@/components/ServicePageLinks";
import VisitUsSection from "@/components/VisitUsSection";
import Hero from "@/components/Hero";
import GuaranteeSection from "@/components/GuaranteeSection";
import WhyChooseUs from "@/components/WhyChooseUs";
import DocumentTypes from "@/components/DocumentTypes";
import AboutSection from "@/components/AboutSection";
import Testimonials from "@/components/Testimonials";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MobileContactBar from "@/components/MobileContactBar";
import { conversionCopy } from "@/lib/conversionCopy";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/lib/useLanguage";
import { useScrollFadeIn } from "@/hooks/useScrollFadeIn";



const Home = () => {
  const { t: originalT, language } = useLanguage();
  const overrides: Record<string, string> = conversionCopy[language].homeText;
  const t = (key: string) => overrides[key] ?? originalT(key);
  const copy = conversionCopy[language];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fade1 = useScrollFadeIn();
  const fade2 = useScrollFadeIn();
  const fade3 = useScrollFadeIn();
  const fade4 = useScrollFadeIn();
  const fade5 = useScrollFadeIn();
  const fade6 = useScrollFadeIn();
  const fade7 = useScrollFadeIn();
  const fade8 = useScrollFadeIn();
  const fade9 = useScrollFadeIn();

  const services = [
    { icon: <FileText className="w-6 h-6 text-primary" />, titleKey: "services.translation.title", descKey: "services.translation.desc", priceKey: "services.translation.price", popular: false },
    { icon: <FileCheck className="w-6 h-6 text-primary" />, titleKey: "services.review.title", descKey: "services.review.desc", priceKey: "services.review.price", popular: false },
    { icon: <Building className="w-6 h-6 text-primary" />, titleKey: "services.package.title", descKey: "services.package.desc", priceKey: "services.package.price", popular: true },
    { icon: <Clock className="w-6 h-6 text-primary" />, titleKey: "services.express.title", descKey: "services.express.desc", priceKey: "services.express.price", popular: false },
  ];

  const steps = [
    { num: "1", titleKey: "how.step1.title", descKey: "how.step1.desc" },
    { num: "2", titleKey: "how.step2.title", descKey: "how.step2.desc" },
    { num: "3", titleKey: "how.step3.title", descKey: "how.step3.desc" },
    { num: "4", titleKey: "how.step4.title", descKey: "how.step4.desc" },
    { num: "5", titleKey: "how.step5.title", descKey: "how.step5.desc" },
  ];

  const faqs = [
    { qKey: "faq.1.q", aKey: "faq.1.a" },
    { qKey: "faq.2.q", aKey: "faq.2.a" },
    { qKey: "faq.3.q", aKey: "faq.3.a" },
    { qKey: "faq.4.q", aKey: "faq.4.a" },
    { qKey: "faq.5.q", aKey: "faq.5.a" },
    { qKey: "faq.6.q", aKey: "faq.6.a" },
    { qKey: "faq.7.q", aKey: "faq.7.a" },
    { qKey: "faq.8.q", aKey: "faq.8.a" },
  ];

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-0">
      <Helmet>
        <title>Thai–Norsk–English | NordicThai Translate</title>
        <meta name="description" content={copy.homeText["footer.tagline"]} />
        <link rel="canonical" href="https://norwaythaitranslate.com/" />
        <script type="application/ld+json">{`
          {"@context":"https://schema.org","@type":"LocalBusiness","name":"Norwegian Immigration & Document Support","description":"Certified Thai, Norwegian and English document translation and guidance for couples and families dealing with Thai or Norwegian authorities","serviceArea":{"@type":"Country","name":"Norway"},"availableLanguage":["Thai","Norwegian","English"],"telephone":"+66821858187","sameAs":["https://wa.me/66821858187"]}
        `}</script>
      </Helmet>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-border bg-white/90 backdrop-blur-[10px]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-primary-foreground font-bold text-sm">
                  NT
                </div>
                <span className="text-base font-bold text-primary hidden sm:block">
                  NordicThai Translate
                </span>
              </Link>
            </div>

            <div className="hidden md:flex items-center gap-8 mx-auto">
              <Link to="/" className="text-sm text-foreground hover:text-primary transition-colors font-medium">{t("nav.home")}</Link>
              <a href="#services" className="text-sm text-foreground hover:text-primary transition-colors font-medium">{t("nav.services")}</a>
              <a href="#how-it-works" className="text-sm text-foreground hover:text-primary transition-colors font-medium">{t("nav.process")}</a>
              <a href="#faq" className="text-sm text-foreground hover:text-primary transition-colors font-medium">{t("nav.faq")}</a>
              <Link to="/blog" className="text-sm text-foreground hover:text-primary transition-colors font-medium">{t("nav.blog")}</Link>
            </div>

            <div className="hidden md:flex items-center gap-3 flex-shrink-0">
              <Link to="/get-quote" className="bg-primary text-primary-foreground text-sm font-semibold px-5 py-2 rounded-lg hover:bg-primary/90 transition-colors">
                {copy.callback}
              </Link>
              <LanguageSwitcher />
              <Link 
                to="/admin-login" 
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                title="Admin Login"
              >
                <User className="w-4 h-4" />
              </Link>
            </div>

            {/* Mobile hamburger */}
            <div className="md:hidden flex items-center gap-2 ml-auto">
              <LanguageSwitcher />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-foreground"
                aria-label={t("nav.menu")}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile menu dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-border py-4 space-y-3">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-foreground hover:text-primary px-2 py-1">{t("nav.home")}</Link>
              <a href="#services" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-foreground hover:text-primary px-2 py-1">{t("nav.services")}</a>
              <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-foreground hover:text-primary px-2 py-1">{t("nav.process")}</a>
              <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-foreground hover:text-primary px-2 py-1">{t("nav.faq")}</a>
              <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-foreground hover:text-primary px-2 py-1">{t("nav.blog")}</Link>
              <Link to="/get-quote" onClick={() => setMobileMenuOpen(false)} className="block bg-primary text-primary-foreground text-sm font-semibold px-5 py-3 rounded-lg text-center mt-2">{copy.callback}</Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <Hero />

      {/* Stats Band — dark gradient */}
      <div ref={fade1.ref} className={fade1.className}>
        <section className="bg-gradient-to-br from-primary to-[hsl(220,60%,18%)] py-8 lg:py-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { numKey: "stats.docsNum", labelKey: "stats.docs" },
                { numKey: "stats.approvedNum", labelKey: "stats.approved" },
                { numKey: "stats.experienceNum", labelKey: "stats.experience" },
                { numKey: "stats.responseNum", labelKey: "stats.response" },
              ].map((stat) => (
                <div key={stat.labelKey}>
                  <p className="text-3xl md:text-4xl font-bold text-primary-foreground mb-1">{t(stat.numKey)}</p>
                  <p className="text-sm text-primary-foreground/70">{stat.labelKey === "stats.docs" ? copy.stats[0] : t(stat.labelKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Guarantee Section */}
      <div ref={fade2.ref} className={fade2.className}>
        <GuaranteeSection />
      </div>

      {/* Why Choose Us Section */}
      <div ref={fade3.ref} className={fade3.className}>
        <WhyChooseUs />
      </div>

      {/* How It Works Section */}
      <div ref={fade4.ref} className={fade4.className}>
        <section id="how-it-works" className="bg-card py-8 lg:py-12 scroll-mt-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-[40px] font-bold text-primary text-center mb-12">
              {t("how.heading")}
            </h2>
            <div className="relative grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-6">
              <div className="hidden md:block absolute top-7 left-[calc(10%+28px)] right-[calc(10%+28px)] border-t-2 border-dashed border-border" />
              {steps.map((step) => (
                <div key={step.num} className="relative text-center">
                  <div className="w-14 h-14 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-5 text-xl font-bold relative z-10">
                    {step.num}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{copy.steps[Number(step.num) - 1][0]}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{copy.steps[Number(step.num) - 1][1]}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Services Section */}
      <div ref={fade5.ref} className={fade5.className}>
        <section id="services" className="bg-secondary py-8 lg:py-12 scroll-mt-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-[40px] font-bold text-primary text-center mb-10">
              {t("services.heading")}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((card) => (
                <div key={card.titleKey} className={`relative bg-card rounded-xl p-10 shadow-[0_2px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.12)] transition-shadow ${card.popular ? 'ring-2 ring-[#16a34a]' : ''}`}>
                  {card.popular && (
                    <span className="absolute top-4 right-4 bg-[#16a34a] text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {t("services.popular")}
                    </span>
                  )}
                  <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center mb-5">
                    {card.icon}
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{t(card.titleKey)}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{t(card.descKey)}</p>
                  <p className="text-sm font-semibold text-primary mb-4">{t(card.priceKey)}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/get-quote">
                <Button className="bg-[#1B2A4A] text-white hover:bg-[#1B2A4A]/90 rounded-xl px-8 py-3 text-base font-semibold">
                  {copy.callback}
                </Button>
              </Link>
            </div>
            <ServicePageLinks />
          </div>
        </section>
      </div>

      {/* Document Types Grid */}
      <div ref={fade6.ref} className={fade6.className}>
        <DocumentTypes />
      </div>

      <div ref={fade7.ref} className={fade7.className}>
        <Testimonials />
      </div>

      {/* About Section */}
      <div ref={fade8.ref} className={fade8.className}>
        <AboutSection />
      </div>

      <div ref={fade9.ref} className={fade9.className}>
        <section id="faq" className="bg-secondary py-8 lg:py-12 scroll-mt-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-[40px] font-bold text-primary text-center mb-10">
              {t("faq.heading")}
            </h2>
            <div className="space-y-3">
              {faqs.map((item, i) => (
                <details key={i} className="group bg-card rounded-xl shadow-[0_2px_16px_rgba(0,0,0,0.08)]">
                  <summary className="flex items-center justify-between cursor-pointer px-8 py-5 text-foreground font-semibold list-none">
                    {t(item.qKey)}
                    <span className="ml-4 text-muted-foreground transition-transform duration-200 group-open:rotate-45 text-xl flex-shrink-0">+</span>
                  </summary>
                  <div className="px-8 pb-5 text-muted-foreground leading-relaxed">
                    {t(item.aKey)}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Bottom CTA Banner */}
      <section className="bg-[hsl(220,60%,15%)] py-10 lg:py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="flex items-center justify-center gap-3 text-primary-foreground/70 text-sm mb-4">
            <span>🆓 {t("cta.micro1")}</span>
            <span className="text-primary-foreground/30">·</span>
            <span>⚡ {t("cta.micro2")}</span>
            <span className="text-primary-foreground/30">·</span>
            <span>✅ {t("cta.micro3")}</span>
          </p>
          <h2 className="text-primary-foreground text-xl md:text-2xl font-semibold mb-3">
            {copy.ctaHeading}
          </h2>
          <p className="text-primary-foreground/70 text-base mb-6">
            {copy.ctaSub}
          </p>
          <Link to="/get-quote">
            <Button size="lg" className="bg-[#25D366] text-white hover:bg-[#25D366]/90 rounded-lg px-8 py-6 text-base font-semibold">
              {copy.callback}
            </Button>
          </Link>
          <p className="text-primary-foreground/60 text-sm mt-4">
            {copy.reassurance}
          </p>
        </div>
      </section>

      <VisitUsSection />

      {/* Footer */}
      <SiteFooter tagline={copy.homeText["footer.tagline"]} />

      {/* AI Chatbot */}
      <MobileContactBar />
    </div>
  );
};

export default Home;
